import React from "react";
//#region types
import { SelectInputItemProps, WintFormConnect } from "models/propTypes/wintFormPropsTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { InvoiceConstants } from "utils/constants";
import { SelfInvoiceFormItemsSectionProps } from "models/propTypes/selfInvoicePropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { ExpandableSection, ExpandableSectionSummary } from "components/common/wintForms";
import { useInvoiceService, useSelfInvoiceService } from "serviceHooks";
import SelfInvoiceItemsFooter from "./itemFooter";
import SelfInvoiceAmountMismatchWarning from "../../alerts/amountMismatch";
import SelfInvoiceItemRow from "./itemsRow";
import { Grid, Table, TableBody, TableContainer } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, UtilsHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const SelfInvoiceFormItemsSection: React.FC<SelfInvoiceFormItemsSectionProps<InvoiceFormTypes.SelfInvoice>> = (
  props
) => {
  const { connect, values, interpretedTotalIncludingTax: invoiceTotal, interpretedItemsTotal } = props;

  const invoiceService = useInvoiceService();
  const selfInvoiceService = useSelfInvoiceService();

  const name = "products";
  const sectionProps = connect(name);
  const [units, setUnits] = React.useState<SelectInputItemProps[]>([]);

  React.useEffect(() => {
    invoiceService.getUnits().then((res) => {
      const newUnits = res.map((u) => ({ value: u.id, label: u.name ?? "" }));
      setUnits([{ value: 0, label: UtilsHelper.getSelectionNoneText() }, ...newUnits.reverse()]);
    });
  }, []);

  const defaultProductRow = React.useMemo(
    (): InvoiceFormTypes.Product => ({
      uniqueId: UtilsHelper.getUniqueId(),
      id: 0,
      quantity: 1,
      unit: units.length > 0 ? (units.find((u) => u.label === InvoiceConstants.DefaultUnit)?.value as number) : 0,
      totalAmountExTax: 0,
      price: 0,
      tax: InvoiceConstants.DefaultVatType,
      taxText: `${InvoiceConstants.DefaultVatType}%`,
      eUVatCategoryId: WintSuperkollAPIModel.EuVatProductType.NotDefined,
    }),
    [units]
  );

  const totalAmountExTax = React.useMemo(
    () => selfInvoiceService.getInvoiceItemTotal(values.products ?? []),
    [values.products]
  );
  const totalTax = selfInvoiceService.getInvoiceItemTaxTotal(values.products ?? []);
  const taxCalculations = InvoiceConstants.VatTypes.map((vat) => {
    return {
      vatType: vat,
      amount: selfInvoiceService.getInvoiceItemTaxTotal(
        values.products?.filter((product) => product.tax === vat) ?? []
      ),
    };
  }).filter((t) => t.amount > 0);
  const total = Number((totalAmountExTax + totalTax).toFixed(2));

  const idField = "uniqueId";

  const updateRow = React.useCallback((index: number, newRow: InvoiceFormTypes.Product, shouldValidate?: boolean) => {
    (connect as WintFormConnect<any>)(`${name}[${index}]`).setValue(newRow, shouldValidate);
  }, []);

  const removeRow = React.useCallback(
    (index: number) => {
      const connection = connect(name);
      const rows = [...(connection.value ?? [])];
      rows.splice(index, 1);
      connection.setValue(rows, true);
    },
    [connect]
  );

  const newRow = React.useMemo(
    (): InvoiceFormTypes.Product => ({
      ...defaultProductRow,
      [idField!]: UtilsHelper.getUniqueId(),
    }),
    [defaultProductRow, idField, values.products?.length]
  );

  const rows = React.useMemo(() => [...(values.products ?? []), newRow], [values.products, newRow]);

  const onUnitSelect = (index: number, value?: number) => {
    const oldRow = index === (values.products ?? []).length ? defaultProductRow : values.products![index];
    const newRow: InvoiceFormTypes.Product = { ...oldRow, unit: value, quantity: !value ? 1 : oldRow.quantity };

    updateRow(index, newRow, true);
  };

  return (
    <>
      <ExpandableSection
        summary={
          <ExpandableSectionSummary
            title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SelfInvoiceRows)}
            subText={`${values.products?.length ?? 0} ${LocalizationHelperInstance.getFormattedMessage(
              TranslationKeys.Common.Rows
            )}`}
          />
        }
        hasErrors={sectionProps.hasErrors}
        {...props.expandableStepProps}
      >
        <Grid
          container
          className={clsx(cssExports.tableListContainer, cssExports.editableTableContainer, cssExports.compactTable)}
          direction="column"
        >
          <TableContainer className={cssExports.tableContainerOuter} classes={{ root: cssExports.tableContainerRoot }}>
            <Table aria-label="simple table">
              <TableBody className={cssExports.tableBody}>
                {rows.map((row, rowIndex) => (
                  <SelfInvoiceItemRow
                    key={`table-row-${row.uniqueId ?? rowIndex}`}
                    connect={connect}
                    index={rowIndex}
                    row={row}
                    updateRow={updateRow.bind(undefined, rowIndex)}
                    removeRow={removeRow}
                    isLastRow={rowIndex === rows.length - 1}
                    onUnitSelect={onUnitSelect.bind(undefined, rowIndex)}
                    units={units}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
        <SelfInvoiceItemsFooter
          total={total}
          totalExTax={totalAmountExTax}
          taxCalculations={taxCalculations}
          interpretedItemsTotal={interpretedItemsTotal}
          currency={values.currency}
          itemsUpdated={sectionProps.touched}
        />
      </ExpandableSection>
      <SelfInvoiceAmountMismatchWarning interpretedTotalIncludingTax={invoiceTotal} currentTotalIncludingTax={total} />
    </>
  );
};

export default SelfInvoiceFormItemsSection;
